import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Building2, Check, X, Trash2 } from 'lucide-react';
import { xero, xeroExtra, xeroExtra2 } from '@/lib/api';

export default function XeroCard() {
  const [cfg, setCfg] = React.useState(null);
  const [clientId, setClientId] = React.useState('');
  const [clientSecret, setClientSecret] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [connecting, setConnecting] = React.useState(false);

  const load = React.useCallback(() => xero.getConfig().then(setCfg), []);
  React.useEffect(() => { load(); }, [load]);

  // Handle OAuth callback return
  React.useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get('xero') === 'connected') {
      load();
      // Clean up the URL
      window.history.replaceState({}, '', '/app/settings');
    }
  }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const patch = {};
      if (clientId) patch.client_id = clientId;
      if (clientSecret) patch.client_secret = clientSecret;
      const r = await xero.saveConfig(patch);
      setCfg(r);
      setClientSecret('');
    } finally { setSaving(false); }
  };

  const connect = async () => {
    setConnecting(true);
    try {
      const r = await xero.oauthStart();
      window.location.href = r.authorize_url;
    } catch (e) {
      window.alert(e?.response?.data?.detail || 'Failed to start Xero OAuth');
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect Xero? Your ImmiPro data will not be affected, but future sync will stop until you reconnect.')) return;
    await xero.disconnect();
    setClientId(''); setClientSecret('');
    load();
  };

  if (!cfg) {
    return <Card><CardContent className="p-6 text-xs text-slate-500"><Loader2 className="inline h-3 w-3 animate-spin"/> Loading…</CardContent></Card>;
  }

  return (
    <Card className="border-slate-200" data-testid="xero-card">
      <CardContent className="p-6">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-[#2ECC9A]" />
            <div className="text-sm font-semibold text-[#1A3A5C]">Xero accounting sync</div>
            {cfg.connected
              ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]"><Check className="mr-1 h-3 w-3"/> Connected {cfg.tenant_name ? `· ${cfg.tenant_name}` : ''}</Badge>
              : <Badge variant="outline" className="border-slate-200 text-slate-500 text-[10px]">Not connected</Badge>}
          </div>
          {cfg.connected && (
            <Button data-testid="xero-disconnect" variant="outline" size="sm" onClick={disconnect}
                    className="h-8 gap-1 border-red-200 text-red-600 text-xs">
              <Trash2 className="h-3 w-3"/> Disconnect
            </Button>
          )}
        </div>

        <p className="mt-2 text-xs text-slate-500">
          Push ImmiPro invoices and trust-ledger movements one-way into your Xero organisation.
          Bring your own OAuth app credentials from the <a className="underline decoration-dotted"
            href="https://developer.xero.com/app/manage" target="_blank" rel="noreferrer">Xero developer portal</a>.
        </p>

        {!cfg.has_credentials && (
          <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-3 text-[11px] text-slate-600">
            <div className="font-semibold text-[#1A3A5C]">Setup — 3 steps</div>
            <ol className="mt-1 list-decimal ml-5 space-y-1">
              <li>Create a Xero developer app (Web app type). Add exactly this redirect URI:
                <br/><span className="font-mono text-[10px] bg-white px-1.5 py-0.5 rounded">{cfg.redirect_uri}</span></li>
              <li>Grant scopes: <span className="font-mono text-[10px]">offline_access accounting.contacts accounting.transactions accounting.settings</span></li>
              <li>Paste the Client ID + Client Secret below and click Save, then Connect Xero.</li>
            </ol>
          </div>
        )}

        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <div>
            <Label className="text-xs">Xero Client ID {cfg.client_id_masked && <span className="ml-1 text-slate-400">(saved · {cfg.client_id_masked})</span>}</Label>
            <Input data-testid="xero-client-id" value={clientId} onChange={(e) => setClientId(e.target.value)}
                   placeholder={cfg.client_id_masked || 'From Xero developer app'} className="mt-1 font-mono text-xs" />
          </div>
          <div>
            <Label className="text-xs">Xero Client Secret {cfg.has_credentials && <span className="ml-1 text-slate-400">(saved · rotate by re-entering)</span>}</Label>
            <Input data-testid="xero-client-secret" value={clientSecret} onChange={(e) => setClientSecret(e.target.value)}
                   type="password" placeholder="From Xero developer app" className="mt-1 font-mono text-xs" />
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2">
          <Button data-testid="xero-save" onClick={save} disabled={saving || (!clientId && !clientSecret)}
                  className="h-9 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90 text-xs">
            {saving ? <Loader2 className="h-3 w-3 animate-spin"/> : 'Save credentials'}
          </Button>
          <Button data-testid="xero-connect" onClick={connect}
                  disabled={connecting || !cfg.has_credentials || cfg.connected}
                  variant="outline"
                  className="h-9 gap-1 border-[#2ECC9A]/40 bg-[#2ECC9A]/5 text-[#0F766E] text-xs">
            {connecting ? <Loader2 className="h-3 w-3 animate-spin"/> : cfg.connected ? 'Already connected' : 'Connect Xero →'}
          </Button>
        </div>

        {cfg.connected && (
          <div className="mt-5 rounded-md border border-slate-200 bg-slate-50 p-3">
            <div className="text-xs font-semibold text-[#1A3A5C]">Two-way sync (webhooks)</div>
            <p className="mt-1 text-[11px] text-slate-500">
              Paste your Xero <b>Webhook signing key</b> below so ImmiPro auto-marks
              invoices as paid the moment they clear in Xero.
              Set the Delivery URL in Xero developer app → Webhooks tab to:
              <br/><span className="font-mono text-[10px] bg-white px-1.5 py-0.5 rounded">{(typeof window !== 'undefined' ? window.location.origin : '')}/api/xero/webhook</span>
            </p>
            <WebhookKeyInput cfg={cfg} onSaved={load} />

            <div className="mt-4 border-t border-slate-200 pt-3">
              <div className="text-xs font-semibold text-[#1A3A5C]">Pull overdue invoices</div>
              <p className="mt-1 text-[11px] text-slate-500">
                On-demand: fetch every overdue invoice from Xero and file an alert in the Bell inbox
                per matter. Runs automatically once per week too.
              </p>
              <PullOverdueButton />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function WebhookKeyInput({ cfg, onSaved }) {
  const [key, setKey] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const submit = async () => {
    if (!key.trim()) return;
    setSaving(true);
    try {
      await xeroExtra.saveWebhookKey(key.trim());
      setSaved(true);
      setKey('');
      setTimeout(() => setSaved(false), 3000);
      onSaved?.();
    } catch (e) {
      window.alert(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };
  return (
    <div className="mt-2 flex items-end gap-2">
      <div className="flex-1">
        <Label className="text-[10px] uppercase tracking-wider text-slate-500">Webhook signing key</Label>
        <Input data-testid="xero-webhook-key" value={key} onChange={(e) => setKey(e.target.value)}
               type="password" placeholder={cfg?.webhook_key_masked || 'From Xero webhook config'}
               className="mt-1 h-9 font-mono text-xs" />
      </div>
      <Button data-testid="xero-webhook-save" onClick={submit} disabled={saving || !key.trim()}
              className="h-9 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90 text-xs">
        {saving ? <Loader2 className="h-3 w-3 animate-spin"/> : (saved ? '✓ Saved' : 'Save key')}
      </Button>
    </div>
  );
}

function PullOverdueButton() {
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const run = async () => {
    setBusy(true); setResult(null);
    try {
      const r = await xeroExtra2.pullOverdue();
      setResult(r);
    } catch (e) {
      setResult({ error: e?.response?.data?.detail || 'Pull failed' });
    } finally { setBusy(false); }
  };
  return (
    <div className="mt-2 flex items-center gap-2">
      <Button data-testid="xero-pull-overdue" onClick={run} disabled={busy}
              variant="outline"
              className="h-9 gap-1 border-amber-200 bg-amber-50 text-amber-800 text-xs">
        {busy ? <Loader2 className="h-3 w-3 animate-spin"/> : 'Pull overdue now'}
      </Button>
      {result && !result.error && (
        <span data-testid="xero-pull-overdue-result" className="text-[11px] text-emerald-700">
          Checked {result.checked} · created {result.alerts_created} alert{result.alerts_created === 1 ? '' : 's'}
        </span>
      )}
      {result?.error && (
        <span className="text-[11px] text-red-600">{result.error}</span>
      )}
    </div>
  );
}
